import styled from '@emotion/styled';
import { FabricPluginName, useFabricEditor } from 'modules/fabric-editor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutBlocksModal } from '../layout-blocks-modal';

const EditorLeftToolbar: React.FC = () => {
  const editor = useFabricEditor();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const layoutPresets = useMemo(() => editor.layoutBlocks?.listPresets() ?? [], [editor]);

  useEffect(() => {
    const off = editor.on('history:changed', (e) => {
      setCanUndo(e.canUndo);
      setCanRedo(e.canRedo);
    });
    return off;
  }, [editor]);

  const addText = useCallback(() => {
    editor.objects.addText({ text: 'Text', left: 200, top: 200, fontSize: 48 });
  }, [editor]);

  const addRect = useCallback(() => {
    editor.objects.addRect({ left: 300, top: 300, width: 240, height: 160, fill: '#4f46e5' });
  }, [editor]);

  const addImagePlaceholder = useCallback(() => {
    editor.placeholders?.addImage({
      placeholderKey: '',
      placeholderLabel: 'Image placeholder',
      left: 200,
      top: 200,
      width: 320,
      height: 240,
    });
  }, [editor]);

  const addTextPlaceholder = useCallback(() => {
    editor.placeholders?.addText({
      placeholderKey: '',
      placeholderLabel: 'Text placeholder',
      sampleText: 'Sample text',
      left: 200,
      top: 200,
      width: 400,
      height: 100,
    });
  }, [editor]);

  const upGrid = useCallback(() => {
    const hasGrid = editor.hasPlugin(FabricPluginName.GRID);
    if (!hasGrid || !editor.grid) throw new Error('Grid plugin is not available');

    const currentStep = editor.grid.getStep();
    editor.grid.setStep(currentStep + 1);
  }, [editor]);

  const downGrid = useCallback(() => {
    const hasGrid = editor.hasPlugin(FabricPluginName.GRID);
    if (!hasGrid || !editor.grid) throw new Error('Grid plugin is not available');

    const currentStep = editor.grid.getStep();
    editor.grid.setStep(currentStep - 1);
  }, [editor]);

  return (
    <Root>
      <ToolButton type="button" onClick={addText}>
        Text
      </ToolButton>
      <ToolButton type="button" onClick={addRect}>
        Rect
      </ToolButton>

      <Divider />

      <ToolButton type="button" onClick={addImagePlaceholder} title="Image placeholder">
        Img PH
      </ToolButton>
      <ToolButton type="button" onClick={addTextPlaceholder} title="Text placeholder">
        Txt PH
      </ToolButton>

      <Divider />

      <ToolButton type="button" onClick={() => setShowLayoutModal(true)} title="Layout blocks">
        Layout
      </ToolButton>

      <Divider />

      <ToolButton type="button" onClick={() => editor.history?.undo()} disabled={!canUndo}>
        Undo
      </ToolButton>
      <ToolButton type="button" onClick={() => editor.history?.redo()} disabled={!canRedo}>
        Redo
      </ToolButton>

      <Divider />

      <ToolButton type="button" onClick={() => editor.zoom?.in()}>
        Zoom +
      </ToolButton>
      <ToolButton type="button" onClick={() => editor.zoom?.out()}>
        Zoom −
      </ToolButton>
      <ToolButton type="button" onClick={() => editor.zoom?.reset()}>
        Reset
      </ToolButton>

      <Divider />

      <ToolButton type="button" onClick={upGrid}>
        +Grid
      </ToolButton>
      <ToolButton type="button" onClick={downGrid}>
        −Grid
      </ToolButton>

      <LayoutBlocksModal
        show={showLayoutModal}
        onClose={() => setShowLayoutModal(false)}
        presets={layoutPresets}
        currentPresetId={editor.layoutBlocks?.getData()?.presetId}
        onPick={(id) => editor.layoutBlocks?.apply(id)}
      />
    </Root>
  );
};

export default React.memo(EditorLeftToolbar);

const Root = styled.div`
  width: 72px;
  height: 100%;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 6px;
  font-size: 12px;
  color: #111827;
  background: #f9fafb;
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;
